import { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Cookies from 'universal-cookie';
import Swal from 'sweetalert2';

const ALLOWED_DOMAIN = 'bellkenzpharma.com';
const GATED_HOSTNAME = 'bkclouderp.com';
const COOKIE_NAME = 'htpass_bk';
const cookieMaxAge = 60 * (60 * 8); // 8 hours

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// On unless explicitly set to "false" — an unset flag keeps the gate enabled.
const GATE_ENABLED =
  String(process.env.REACT_APP_GOOGLE_AUTH_GATE_ENABLED || 'true')
    .trim()
    .toLowerCase() !== 'false';

/**
 * Decode the payload of a Google ID token (JWT) without verifying its
 * signature. Client-side only — this gate has the same (cosmetic) scope as the
 * htpass gate it replaces. Returns null when the token is malformed.
 */
const decodeJwtPayload = (token) => {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
};

const ModalGoogleAuth = () => {
  const cookies = new Cookies();
  const buttonRef = useRef(null);

  const [show, setShow] = useState(false);
  const [gisReady, setGisReady] = useState(false);

  // Decide whether the gate should be shown (when there is no live cookie).
  // Gated on the bkclouderp.com hostname — this covers both the prod
  // (bkclouderp.com) and qa (bkclouderp.com:42763) servers, regardless of port.
  useEffect(() => {
    const shouldGate =
      GATE_ENABLED && window.location.hostname === GATED_HOSTNAME;
    if (shouldGate && !cookies.get(COOKIE_NAME)) {
      const timer = setTimeout(() => setShow(true), 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wait for the Google Identity Services script (loaded in index.html) to be
  // ready before we try to initialize it.
  useEffect(() => {
    if (!show) return;

    if (window.google?.accounts?.id) {
      setGisReady(true);
      return;
    }

    const interval = setInterval(() => {
      if (window.google?.accounts?.id) {
        setGisReady(true);
        clearInterval(interval);
      }
    }, 200);

    // Give the CDN script a reasonable window to load before surfacing an error.
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.google?.accounts?.id) {
        Swal.fire({
          title: 'Secure Access',
          text: 'Unable to load Google sign-in. Please check your connection and reload the page.',
          icon: 'error',
        });
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [show]);

  // Initialize GIS and render the official Google button once everything is ready.
  useEffect(() => {
    if (!show || !gisReady) return;

    if (!CLIENT_ID) {
      // Fail safe: a misconfigured build should be obvious, not silently open.
      Swal.fire({
        title: 'Secure Access',
        text: 'Sign-in is not configured (missing client id). Please contact IT.',
        icon: 'error',
      });
      return;
    }

    window.google.accounts.id.initialize({
      client_id: CLIENT_ID,
      callback: handleCredentialResponse,
      hd: ALLOWED_DOMAIN,
      auto_select: false,
    });

    if (buttonRef.current) {
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'filled_blue',
        size: 'large',
        text: 'signin_with',
        shape: 'pill',
        width: 320,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, gisReady]);

  const handleCredentialResponse = (response) => {
    const payload = decodeJwtPayload(response?.credential);

    const isValid =
      payload &&
      payload.email_verified === true &&
      payload.hd === ALLOWED_DOMAIN &&
      String(payload.email).toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`);

    if (isValid) {
      cookies.set(COOKIE_NAME, 1, { path: '/', maxAge: cookieMaxAge });
      setShow(false);
      return;
    }

    // Reject and make sure the bad account isn't auto-reselected next time.
    window.google?.accounts?.id?.disableAutoSelect();
    Swal.fire({
      title: 'Access Denied',
      text: `This account is not part of the ${ALLOWED_DOMAIN} organization. Please sign in with your corporate Gmail account.`,
      icon: 'error',
    });
  };

  return (
    <Modal show={show} backdrop="static" keyboard={false} centered>
      <Modal.Header>
        <Modal.Title>🔐 Secure Access</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">
          Authenticate with your corporate Gmail account
        </p>
        <p className="small">
          We are improving our security measure for our internal system. As an
          added layer of security, we are implementing corporate Gmail
          verification. You must be logged in with a corporate Gmail account to
          access this resource. Please sign in below to authenticate.
        </p>

        <div className="d-flex justify-content-center my-3" ref={buttonRef} />

        {!gisReady && (
          <p className="small text-center text-muted">
            Loading Google sign-in…
          </p>
        )}

        <div className="mt-3 p-2 bg-light rounded">
          <strong className="small">✔ Allowed Domains:</strong>
          <div className="small">• {ALLOWED_DOMAIN}</div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ModalGoogleAuth;
